/**
 * zip.js — a tiny store-only ZIP writer for carousel exports.
 *
 * Slides are JPEG or PNG, which are already compressed: deflating them again
 * would cost time and save nothing, so every entry is stored verbatim (method 0).
 * That keeps this to one small file instead of a dependency in every page load.
 *
 * Plain ZIP, no Zip64 and no data descriptors — a carousel is at most a few
 * hundred megabytes, well inside the 4GB fields.
 */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes) {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
}

// MS-DOS time and date, as ZIP has stored them since 1989.
function dosStamp(date) {
  const d = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date(2026, 0, 1);
  const year = Math.max(1980, d.getFullYear());
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (Math.floor(d.getSeconds() / 2) & 0x1F),
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

// Names are written as UTF-8 with the language-encoding flag set, so an accent
// in a carousel's name survives the round trip.
function encodeName(name) {
  return new TextEncoder().encode(String(name).replace(/^\/+/, '').replace(/\.\.\//g, ''));
}

const UTF8_FLAG = 0x0800;

function localHeader(entry) {
  const buf = new Uint8Array(30 + entry.name.length);
  const v = new DataView(buf.buffer);
  v.setUint32(0, 0x04034b50, true);   // local file header
  v.setUint16(4, 20, true);           // version needed
  v.setUint16(6, UTF8_FLAG, true);
  v.setUint16(8, 0, true);            // stored
  v.setUint16(10, entry.time, true);
  v.setUint16(12, entry.date, true);
  v.setUint32(14, entry.crc, true);
  v.setUint32(18, entry.size, true);
  v.setUint32(22, entry.size, true);
  v.setUint16(26, entry.name.length, true);
  v.setUint16(28, 0, true);
  buf.set(entry.name, 30);
  return buf;
}

function centralHeader(entry) {
  const buf = new Uint8Array(46 + entry.name.length);
  const v = new DataView(buf.buffer);
  v.setUint32(0, 0x02014b50, true);   // central directory header
  v.setUint16(4, 20, true);           // version made by
  v.setUint16(6, 20, true);           // version needed
  v.setUint16(8, UTF8_FLAG, true);
  v.setUint16(10, 0, true);           // stored
  v.setUint16(12, entry.time, true);
  v.setUint16(14, entry.date, true);
  v.setUint32(16, entry.crc, true);
  v.setUint32(20, entry.size, true);
  v.setUint32(24, entry.size, true);
  v.setUint16(28, entry.name.length, true);
  v.setUint16(30, 0, true);           // extra
  v.setUint16(32, 0, true);           // comment
  v.setUint16(34, 0, true);           // disk
  v.setUint16(36, 0, true);           // internal attrs
  v.setUint32(38, 0, true);           // external attrs
  v.setUint32(42, entry.offset, true);
  buf.set(entry.name, 46);
  return buf;
}

function endRecord(count, size, offset) {
  const buf = new Uint8Array(22);
  const v = new DataView(buf.buffer);
  v.setUint32(0, 0x06054b50, true);   // end of central directory
  v.setUint16(8, count, true);
  v.setUint16(10, count, true);
  v.setUint32(12, size, true);
  v.setUint32(16, offset, true);
  return buf;
}

/**
 * files: [{ name, blob }] (or { name, bytes }) -> Blob, one stored entry each.
 * Duplicate names get " (2)" before the extension, as a download folder would.
 */
export async function makeZip(files, { date } = {}) {
  const stamp = dosStamp(date || new Date());
  const parts = [];
  const central = [];
  const used = new Set();
  let offset = 0;

  for (const f of files) {
    const bytes = f.bytes ? f.bytes : new Uint8Array(await f.blob.arrayBuffer());
    let label = String(f.name || 'slide');
    if (used.has(label)) {
      const dot = label.lastIndexOf('.');
      const stem = dot > 0 ? label.slice(0, dot) : label;
      const ext = dot > 0 ? label.slice(dot) : '';
      let n = 2;
      while (used.has(stem + ' (' + n + ')' + ext)) n++;
      label = stem + ' (' + n + ')' + ext;
    }
    used.add(label);
    const entry = {
      name: encodeName(label),
      crc: crc32(bytes),
      size: bytes.length,
      offset,
      time: stamp.time,
      date: stamp.date,
    };
    const head = localHeader(entry);
    parts.push(head, bytes);
    central.push(centralHeader(entry));
    offset += head.length + bytes.length;
  }

  const cdSize = central.reduce((n, c) => n + c.length, 0);
  return new Blob([...parts, ...central, endRecord(central.length, cdSize, offset)], { type: 'application/zip' });
}

/** "shoreline" -> "shoreline.zip" */
export function zipFilename(prefix) {
  const stem = String(prefix || 'carousel').trim().replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '');
  return (stem || 'carousel') + '.zip';
}
